/**
 * LocaleSelector - Country/Language picker for trial form
 * 
 * Adapted from turn-v1/src/components/LocaleSelector/LocaleSelector.tsx
 * Restyled for dark glassmorphism theme
 * 
 * Uses state-based selection (callback) instead of URL routing
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Globe, X } from 'lucide-react';
import { locales, type LocaleConfig, defaultLocale } from '@/lib/locales';

interface CountryGroup {
  country: string;
  flag: string;
  languages: LocaleConfig[];
}

interface LocaleSelectorProps {
  /** Currently selected locale */
  value?: string;
  /** Callback when locale is selected */
  onChange: (locale: LocaleConfig) => void;
  /** Optional className for the trigger button */
  className?: string;
  /** Whether to show as full-page modal (for step 1 of trial) */
  fullPage?: boolean;
}

export const LocaleSelector: React.FC<LocaleSelectorProps> = ({
  value,
  onChange,
  className = '',
  fullPage = false,
}) => {
  const [isOpen, setIsOpen] = useState(fullPage);
  const currentLocale = useMemo(() => 
    locales.find(l => l.locale === value) || locales.find(l => l.locale === defaultLocale),
    [value]
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Group locales by country
  const countryGroups = useMemo(() => {
    const groups = new Map<string, CountryGroup>();
    
    locales.forEach((locale) => {
      if (!groups.has(locale.country)) {
        groups.set(locale.country, {
          country: locale.country,
          flag: locale.flag,
          languages: [],
        });
      }
      groups.get(locale.country)!.languages.push(locale);
    });

    // Sort languages within each country (English first if available, then alphabetically)
    groups.forEach((group) => {
      group.languages.sort((a, b) => {
        if (a.language === 'English') return -1;
        if (b.language === 'English') return 1;
        return a.language.localeCompare(b.language);
      });
    });

    return Array.from(groups.values()).sort((a, b) => 
      a.country.localeCompare(b.country)
    );
  }, []);

  // Lock body scroll when modal is open (non-fullPage mode).
  // Click-outside is handled by the backdrop's onClick in the portal,
  // NOT by a mousedown listener on dropdownRef. The old mousedown approach
  // broke after refactoring to createPortal because the modal renders at
  // document.body, outside dropdownRef.current, causing false-positive closes.
  useEffect(() => {
    if (isOpen && !fullPage) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      if (!fullPage) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, fullPage]);

  const handleSelectLocale = (locale: LocaleConfig) => {
    onChange(locale);
    if (!fullPage) {
      setIsOpen(false);
    }
  };

  // Full page mode for initial selection
  if (fullPage) {
    return (
      <div className="min-h-screen bg-[#FAF6F0] flex flex-col">
        {/* Header */}
        <div className="text-center pt-12 pb-8 px-4">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Globe className="w-6 h-6 text-[#636366]" />
            <h1 className="text-sm font-medium tracking-[0.2em] text-[#636366] uppercase">
              Language
            </h1>
          </div>
          <p className="text-[#8E8E93] text-sm">
            Select your preferred language to continue
          </p>
        </div>

        {/* Country Grid */}
        <div className="flex-1 overflow-y-auto px-4 pb-8">
          <div className="max-w-5xl mx-auto">
            <div 
              className="rounded-2xl p-6 md:p-8"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(40px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2">
                {countryGroups.map((group) => {
                  const isCountrySelected = group.languages.some(l => l.locale === currentLocale?.locale);
                  
                  return (
                    <div key={group.country} className="py-1">
                      <div className="flex items-start gap-3 hover:bg-black/[0.03] transition-colors rounded-lg px-3 py-2 -mx-3">
                        <span className={`text-xl flex-shrink-0 relative ${isCountrySelected ? 'pb-1' : ''}`}>
                          {group.flag}
                          {isCountrySelected && (
                            <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#5C2D91] rounded-full"></span>
                          )}
                        </span>
                        <div className="flex-1 text-left min-w-0">
                          <button
                            onClick={() => handleSelectLocale(group.languages[0])}
                            className="text-sm text-[#1C1C1E] font-medium hover:text-[#5C2D91] transition-colors"
                          >
                            {group.country}
                          </button>
                          <span className="text-sm text-[#636366] ml-1">
                            {' ('}
                            {group.languages.map((locale, idx) => {
                              const isSelected = locale.locale === currentLocale?.locale;
                              return (
                                <span key={locale.locale}>
                                  {idx > 0 && ' '}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSelectLocale(locale);
                                    }}
                                    className={`hover:text-[#5C2D91] transition-colors ${
                                      isSelected ? 'font-semibold text-[#5C2D91]' : 'text-[#636366]'
                                    }`}
                                  >
                                    {locale.language}
                                  </button>
                                </span>
                              );
                            })}
                            {')'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Dropdown mode for inline selection
  // Uses portal to render modal at document.body level, avoiding
  // z-index and overflow issues on tablet/desktop.
  const dropdownModal = isOpen ? createPortal(
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/25 backdrop-blur-sm"
        onClick={() => setIsOpen(false)}
        aria-hidden="true"
      />
      
      {/* Modal - fixed centered on all screen sizes */}
      <div 
        className="relative w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden rounded-2xl bg-[#FAF6F0] border border-black/[0.08] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-black/[0.08] flex-shrink-0">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#636366]" />
            <span className="text-sm font-medium text-[#636366] uppercase tracking-wider">
              Language
            </span>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-black/[0.05] rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-[#636366]" />
          </button>
        </div>

        {/* Content - scrollable */}
        <div className="overflow-y-auto flex-1 min-h-0 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-1">
            {countryGroups.map((group) => {
              const isCountrySelected = group.languages.some(l => l.locale === currentLocale?.locale);
              
              return (
                <div key={group.country} className="py-0.5">
                  <div className="flex items-start gap-2 hover:bg-black/[0.03] transition-colors rounded-md px-2 py-1.5 -mx-2">
                    <span className={`text-lg flex-shrink-0 relative ${isCountrySelected ? 'pb-1' : ''}`}>
                      {group.flag}
                      {isCountrySelected && (
                        <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#5C2D91] rounded-full"></span>
                      )}
                    </span>
                    <div className="flex-1 text-left min-w-0">
                      <button
                        onClick={() => handleSelectLocale(group.languages[0])}
                        className="text-sm text-[#1C1C1E] font-medium hover:text-[#5C2D91] transition-colors"
                      >
                        {group.country}
                      </button>
                      <span className="text-sm text-[#636366] ml-1">
                        {' ('}
                        {group.languages.map((locale, idx) => {
                          const isSelected = locale.locale === currentLocale?.locale;
                          return (
                            <span key={locale.locale}>
                              {idx > 0 && ' '}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectLocale(locale);
                                }}
                                className={`hover:text-[#5C2D91] transition-colors ${
                                  isSelected ? 'font-semibold text-[#5C2D91]' : ''
                                }`}
                              >
                                {locale.language}
                              </button>
                            </span>
                          );
                        })}
                        {')'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 text-sm font-medium text-[#1C1C1E] hover:text-[#1C1C1E] transition-colors ${className}`}
      >
        <Globe className="w-4 h-4" />
        <span className="border-b border-[#5C2D91]/50 hover:border-[#5C2D91]">
          {currentLocale?.displayName || 'United States / English'}
        </span>
      </button>
      {dropdownModal}
    </div>
  );
};

export default LocaleSelector;
